import type {
  SecurityUpdatePreferencesRequest,
  SecurityUpdatePreferencesResponse
} from 'invApi';
import { useReactMutation } from '../api/reactQuery';
import { sendData } from '../api/services.api';

const UPDATE_PREFERENCES_MUTATION_KEY = 'updatePreferences';

/**
 * React Query mutation for persisting the authenticated user's preferences.
 *
 * Sends the full preference set to `Security/UpdatePreferences`; callers are
 * responsible for merging the change into the existing preferences (see
 * `SessionHelpers.buildPreferencesWith*`) so unrelated preferences are not
 * clobbered.
 *
 * Errors are unhandled by default so callers can treat the write as
 * best-effort; the loading indicator is disabled so background preference
 * writes do not flash a global spinner.
 *
 * Endpoint: Security/UpdatePreferences
 */
export function useUpdatePreferencesMutation() {
  return useReactMutation<
    SecurityUpdatePreferencesResponse,
    SecurityUpdatePreferencesRequest
  >(
    [UPDATE_PREFERENCES_MUTATION_KEY],
    (request: SecurityUpdatePreferencesRequest) =>
      sendData(
        { path: 'Security/UpdatePreferences' },
        { payload: request, faults: undefined as never }
      ),
    {},
    {
      useLoadingIndicator: false,
      errorHandling: 'unhandled'
    }
  );
}
